import { SelectInput, TextInput } from '@components/inputs'
import {
  Divider,
  Grid,
  TextField,
  IconButton,
  Typography,
  Box,
  FormControl,
  Card,
  CardContent,
  MenuItem,
} from '@mui/material'
import { useSelector } from '@state/store'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Button } from '@components/buttons'
import { SpaceVertical } from '@utils/Spacing'
import { useDispatch } from 'react-redux'
import { findAll as findAllSites } from '@state/thunks/siteThunk'

import {
  CreateQuestionDto,
  QuestionDto,
  UpdateQuestionDto,
  ChoiceDto as ApiChoiceDto,
} from '@services/api'

import { addQuestionSchema } from '@utils/schemas'
import { yupResolver } from '@hookform/resolvers/yup'
import { createQuestion, updateQuestion } from '@state/thunks/questionThunk'
import { questionType } from '@utils/constants'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import { v4 as uuidv4 } from 'uuid'
import { PopConfirm } from '@components/common/PopConfirm'

// Define our custom ChoiceDto with uid instead of id
interface ChoiceDto {
  id: string
  text: string
}

// Converter function to convert between API and internal format
const toApiChoice = (choice: ChoiceDto): ApiChoiceDto => ({
  id: choice.id,
  text: choice.text,
})

const fromApiChoice = (apiChoice: ApiChoiceDto): ChoiceDto => ({
  id: apiChoice.id,
  text: apiChoice.text,
})

// Extend the DTO interfaces to include properties needed for the form
interface ExtendedQuestionDto extends QuestionDto {
  childQuestions?: CreateQuestionDto[]
  askSecondaryIf?: string
}

interface ExtendedCreateQuestionDto extends CreateQuestionDto {
  id?: number
  childQuestions?: ExtendedCreateQuestionDto[]
  askSecondaryIf?: string
}

interface ExtendedUpdateQuestionDto extends UpdateQuestionDto {
  childQuestions?: ExtendedCreateQuestionDto[]
  askSecondaryIf?: string
}

// Interface for child question in the form
interface ChildQuestion {
  id: number
  question: string
  type: string
  choices?: ChoiceDto[]
  siteId?: number
  askSecondaryIf: string
}

type AddQuestionFormProps = {
  row?: ExtendedQuestionDto
  onClose?: () => void
  selectedExamId?: number
}

export function QuestionForm({
  row,
  onClose,
  selectedExamId,
}: AddQuestionFormProps) {
  const dispatch = useDispatch()

  const [selectedExams, setSelectedExams] = useState<number[]>([])
  const [choices, setChoices] = useState<ChoiceDto[]>([])
  const [newChoice, setNewChoice] = useState<string>('')
  const [childQuestions, setChildQuestions] = useState<ChildQuestion[]>([])

  const [showSecondaryForm, setShowSecondaryForm] = useState<boolean>(false)

  const [newSecondaryQuestion, setNewSecondaryQuestion] = useState<{
    question: string
    type: string
    choices: ChoiceDto[]
    newChoiceText: string
    askSecondaryIf: string
  }>({
    question: '',
    type: 'OPEN',
    choices: [],
    newChoiceText: '',
    askSecondaryIf: '',
  })

  // Form control
  const {
    handleSubmit,
    formState: { errors },
    control,
    watch,
  } = useForm<ExtendedQuestionDto>({
    resolver: yupResolver(addQuestionSchema),
    defaultValues: {
      ...row,
      siteId: row?.site?.id?.toString() as any,
    },
  })

  const selectedType = (watch as any)('type')
  const isChoiceType =
    selectedType === 'CHOICES' || selectedType === 'MULTICHOICES'
  const canHaveSecondaryQuestions =
    selectedType === 'CLOSED' || selectedType === 'CHOICES'

  const onSubmit = (values: ExtendedQuestionDto) => {
    const childQuestionsData = childQuestions.map((childQuestion) => ({
      question: childQuestion.question,
      type: childQuestion.type,
      choices: childQuestion.choices?.map(toApiChoice) || [],
      siteId: parseInt(values.siteId.toString()),
      askSecondaryIf: childQuestion.askSecondaryIf,
    }))

    if (row?.id) {
      const updateQuestionDto: ExtendedUpdateQuestionDto = {
        ...row,
        question: values.question,
        type: values.type,
        target: values.target || undefined,
        siteId: parseInt(values.siteId.toString()),
        examIds: [
          ...selectedExams,
          ...(selectedExamId !== undefined ? [selectedExamId] : []),
        ],
        choices: isChoiceType ? choices.map(toApiChoice) : [],
        childQuestions: canHaveSecondaryQuestions ? childQuestionsData : [],
      }
      dispatch(updateQuestion({ id: row.id, body: updateQuestionDto }))
    } else {
      const createQuestionDto: ExtendedCreateQuestionDto = {
        question: values.question,
        type: values.type,
        target: values.target || undefined,
        siteId: parseInt(values.siteId.toString()),
        examIds: [
          ...selectedExams,
          ...(selectedExamId !== undefined ? [selectedExamId] : []),
        ],
        choices: isChoiceType ? choices.map(toApiChoice) : [],
        childQuestions: canHaveSecondaryQuestions ? childQuestionsData : [],
      }
      dispatch(createQuestion(createQuestionDto))
    }
    if (onClose) {
      onClose()
    }
  }

  // Reducers
  const { sites } = useSelector(({ site }) => ({
    sites: site.sites.datas,
  }))
  // Map all sites
  const sitesOptions = useMemo(
    () =>
      sites.map((site) => ({
        value: site.id as number,
        label: site.label,
      })),
    [sites],
  )
  useEffect(() => {
    if (row) {
      setSelectedExams(row.exams.map((exam) => exam.id))
      if (row.choices && row.choices.length > 0) {
        setChoices(row.choices.map(fromApiChoice))
      }
      // Load child questions if available
      if (row.childQuestions && row.childQuestions.length > 0) {
        const childQuestions = row.childQuestions.map((sq) => ({
          id: Date.now() + Math.random(),
          question: sq.question,
          type: sq.type,
          choices: sq.choices?.map(fromApiChoice) || [],
          siteId: sq.siteId,
          askSecondaryIf: sq.askSecondaryIf || '',
        }))
        setChildQuestions(childQuestions)
      }
    }
  }, [row])

  useEffect(() => {
    dispatch(findAllSites())
  }, [])

  const handleCancelPress = () => {
    if (onClose) {
      onClose()
    }
    // Closes the update form - Datagrid didnt provide a prop function to close form ()
    const el = (document as Document).querySelector(
      `.MuiDataGrid-row[data-id="${row?.id}"] .MuiIconButton-root.MuiIconButton-sizeSmall`,
    ) as any

    if (el) {
      el.click()
    }
  }

  const handleAddChoice = () => {
    if (newChoice.trim() === '') return
    setChoices([...choices, { id: uuidv4(), text: newChoice.trim() }])
    setNewChoice('')
  }

  const getChildQuestionsForChoice = (choiceId: string) => {
    return childQuestions.filter((q) => q.askSecondaryIf === choiceId)
  }

  const getChildQuestionsForSecondaryChoice = (
    questionId: number,
    choiceId: string,
  ) => {
    // In case we later support nested child questions, this would check for them
    // For now, it returns an empty array as we don't have nested child questions
    return []
  }

  const handleRemoveChoice = (id: string) => {
    setChoices(choices.filter((choice) => choice.id !== id))
    // Update child questions that depend on this choice
    setChildQuestions(childQuestions.filter((q) => q.askSecondaryIf !== id))
  }

  const handleAddSecondaryQuestion = () => {
    if (!newSecondaryQuestion.question) return

    const newQuestion: ChildQuestion = {
      id: Date.now(),
      question: newSecondaryQuestion.question,
      type: newSecondaryQuestion.type,
      choices: newSecondaryQuestion.choices,
      askSecondaryIf: newSecondaryQuestion.askSecondaryIf,
    }

    setChildQuestions([...childQuestions, newQuestion])
    setNewSecondaryQuestion({
      question: '',
      type: 'OPEN',
      choices: [],
      newChoiceText: '',
      askSecondaryIf: '',
    })
    setShowSecondaryForm(false)
  }

  const handleAddSecondaryChoice = () => {
    if (!newSecondaryQuestion.newChoiceText.trim()) return

    setNewSecondaryQuestion({
      ...newSecondaryQuestion,
      choices: [
        ...newSecondaryQuestion.choices,
        {
          id: uuidv4(),
          text: newSecondaryQuestion.newChoiceText.trim(),
        },
      ],
      newChoiceText: '',
    })
  }

  const handleRemoveSecondaryChoice = (choiceId: string) => {
    setNewSecondaryQuestion({
      ...newSecondaryQuestion,
      choices: newSecondaryQuestion.choices.filter((c) => c.id !== choiceId),
    })
  }

  const handleUpdateSecondaryQuestion = (
    questionId: number,
    field: string,
    value: any,
  ) => {
    setChildQuestions(
      childQuestions.map((q) =>
        q.id === questionId ? { ...q, [field]: value } : q,
      ),
    )
  }

  const handleRemoveSecondaryQuestion = (questionId: number) => {
    setChildQuestions(childQuestions.filter((q) => q.id !== questionId))
  }

  const handleAddSecondaryQuestionChoice = (
    questionId: number,
    choiceText: string,
  ) => {
    if (!choiceText.trim()) return

    setChildQuestions(
      childQuestions.map((q) => {
        if (q.id === questionId) {
          return {
            ...q,
            choices: [
              ...(q.choices || []),
              {
                id: uuidv4(),
                text: choiceText.trim(),
              },
            ],
          }
        }
        return q
      }),
    )
  }

  const handleRemoveSecondaryQuestionChoice = (
    questionId: number,
    choiceUid: string,
  ) => {
    setChildQuestions(
      childQuestions.map((q) => {
        if (q.id === questionId && q.choices) {
          return {
            ...q,
            choices: q.choices.filter((choice) => choice.id !== choiceUid),
          }
        }
        return q
      }),
    )
  }

  const renderSecondaryQuestionEditor = (childQuestion: ChildQuestion) => {
    const isChoiceTypeQuestion =
      childQuestion.type === 'CHOICES' || childQuestion.type === 'MULTICHOICES'

    // Check if this secondary question has choices that other questions might depend on
    const hasChoices = childQuestion.choices && childQuestion.choices.length > 0

    return (
      <Card variant="outlined">
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={10}>
              <TextField
                fullWidth
                value={childQuestion.question}
                onChange={(e) =>
                  handleUpdateSecondaryQuestion(
                    childQuestion.id,
                    'question',
                    e.target.value,
                  )
                }
                placeholder="Enter secondary question"
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={2}>
              {hasChoices ? (
                <PopConfirm
                  onConfirm={() =>
                    handleRemoveSecondaryQuestion(childQuestion.id)
                  }
                  title="Supprimer la question secondaire"
                  description="Êtes-vous sûr de vouloir supprimer cette question et tous ses choix ?"
                  okText="Supprimer"
                  cancelText="Annuler"
                >
                  <IconButton color="error">
                    <DeleteIcon />
                  </IconButton>
                </PopConfirm>
              ) : (
                <IconButton
                  color="error"
                  onClick={() =>
                    handleRemoveSecondaryQuestion(childQuestion.id)
                  }
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <TextField
                  name={'secondaryType'}
                  select
                  value={childQuestion.type}
                  onChange={(e: any) =>
                    handleUpdateSecondaryQuestion(
                      childQuestion.id,
                      'type',
                      e.target.value,
                    )
                  }
                  label="Question Type"
                >
                  {questionType.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ mb: 1 }}
                >
                  Ask when
                </Typography>
                <TextField
                  select
                  fullWidth
                  value={childQuestion.askSecondaryIf}
                  onChange={(e) =>
                    handleUpdateSecondaryQuestion(
                      childQuestion.id,
                      'askSecondaryIf',
                      e.target.value,
                    )
                  }
                  variant="outlined"
                  size="small"
                >
                  {selectedType === 'CLOSED'
                    ? [
                        <MenuItem key="yes" value="yes">
                          When answered &quot;Yes&quot;
                        </MenuItem>,
                        <MenuItem key="no" value="no">
                          When answered &quot;No&quot;
                        </MenuItem>,
                      ]
                    : choices.map((choice) => (
                        <MenuItem key={choice.id} value={choice.id}>
                          When &quot;{choice.text}&quot; is selected
                        </MenuItem>
                      ))}
                </TextField>
              </FormControl>
            </Grid>

            {isChoiceTypeQuestion && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                  Choices
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={10}>
                    <TextField
                      fullWidth
                      placeholder="Enter choice"
                      variant="outlined"
                      size="small"
                      onKeyPress={(e: any) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          handleAddSecondaryQuestionChoice(
                            childQuestion.id,
                            e.target.value,
                          )
                          e.target.value = ''
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <IconButton
                      color="primary"
                      onClick={(e) => {
                        const input = e.currentTarget
                          .closest('.MuiGrid-container')
                          ?.querySelector('input')
                        if (input) {
                          handleAddSecondaryQuestionChoice(
                            childQuestion.id,
                            input.value,
                          )
                          input.value = ''
                        }
                      }}
                    >
                      <AddIcon />
                    </IconButton>
                  </Grid>
                </Grid>

                <Box sx={{ mt: 1 }}>
                  {childQuestion.choices &&
                    childQuestion.choices.map((choice) => {
                      const dependentQuestions =
                        getChildQuestionsForSecondaryChoice(
                          childQuestion.id,
                          choice.id,
                        )
                      const hasDependentQuestions =
                        dependentQuestions.length > 0

                      return (
                        <Box
                          key={choice.id}
                          sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
                        >
                          <TextField
                            fullWidth
                            value={choice.text}
                            onChange={(e) => {
                              const updatedChoices =
                                childQuestion.choices?.map((c) =>
                                  c.id === choice.id
                                    ? { ...c, text: e.target.value }
                                    : c,
                                ) || []
                              handleUpdateSecondaryQuestion(
                                childQuestion.id,
                                'choices',
                                updatedChoices,
                              )
                            }}
                            variant="outlined"
                            size="small"
                          />
                          {hasDependentQuestions ? (
                            <PopConfirm
                              onConfirm={() =>
                                handleRemoveSecondaryQuestionChoice(
                                  childQuestion.id,
                                  choice.id,
                                )
                              }
                              title="Supprimer le choix et les questions dépendantes"
                              description={`Ce choix a ${dependentQuestions.length} question(s) dépendante(s). Êtes-vous sûr de vouloir tout supprimer ?`}
                              okText="Supprimer tout"
                              cancelText="Annuler"
                            >
                              <IconButton color="error" size="small">
                                <DeleteIcon />
                              </IconButton>
                            </PopConfirm>
                          ) : (
                            <IconButton
                              color="error"
                              onClick={() =>
                                handleRemoveSecondaryQuestionChoice(
                                  childQuestion.id,
                                  choice.id,
                                )
                              }
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </Box>
                      )
                    })}
                </Box>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>
    )
  }

  const renderSecondaryQuestionForm = () => {
    if (!showSecondaryForm) {
      return (
        <Button
          onClick={() => setShowSecondaryForm(true)}
          color="primary"
          text="Add Secondary Question"
          textColor="white"
          fullWidth={false}
          sx={{ mb: 2 }}
        />
      )
    }

    const isSecondaryChoiceType =
      newSecondaryQuestion.type === 'CHOICES' ||
      newSecondaryQuestion.type === 'MULTICHOICES'

    return (
      <Card variant="outlined" sx={{ mb: 2, mt: 1 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                value={newSecondaryQuestion.question}
                onChange={(e) =>
                  setNewSecondaryQuestion({
                    ...newSecondaryQuestion,
                    question: e.target.value,
                  })
                }
                placeholder="Enter secondary question"
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ mb: 1 }}
                >
                  Question Type
                </Typography>
                <TextField
                  select
                  fullWidth
                  value={newSecondaryQuestion.type}
                  onChange={(e) =>
                    setNewSecondaryQuestion({
                      ...newSecondaryQuestion,
                      type: e.target.value,
                      choices:
                        e.target.value === 'CHOICES' ||
                        e.target.value === 'MULTICHOICES'
                          ? newSecondaryQuestion.choices
                          : [],
                    })
                  }
                  variant="outlined"
                  size="small"
                >
                  {questionType.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ mb: 1 }}
                >
                  Ask when
                </Typography>
                <TextField
                  select
                  fullWidth
                  value={newSecondaryQuestion.askSecondaryIf}
                  onChange={(e) =>
                    setNewSecondaryQuestion({
                      ...newSecondaryQuestion,
                      askSecondaryIf: e.target.value,
                    })
                  }
                  variant="outlined"
                  size="small"
                >
                  {selectedType === 'CLOSED'
                    ? [
                        <MenuItem key="yes" value="yes">
                          When answered &quot;Yes&quot;
                        </MenuItem>,
                        <MenuItem key="no" value="no">
                          When answered &quot;No&quot;
                        </MenuItem>,
                      ]
                    : choices.map((choice) => (
                        <MenuItem key={choice.id} value={choice.id}>
                          When &quot;{choice.text}&quot; is selected
                        </MenuItem>
                      ))}
                </TextField>
              </FormControl>
            </Grid>

            {isSecondaryChoiceType && (
              <Grid item xs={12}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ mb: 1, display: 'block' }}
                >
                  Choices
                </Typography>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={10}>
                    <TextField
                      fullWidth
                      value={newSecondaryQuestion.newChoiceText}
                      onChange={(e) =>
                        setNewSecondaryQuestion({
                          ...newSecondaryQuestion,
                          newChoiceText: e.target.value,
                        })
                      }
                      placeholder="Enter choice text"
                      variant="outlined"
                      size="small"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          handleAddSecondaryChoice()
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <IconButton
                      color="primary"
                      onClick={handleAddSecondaryChoice}
                      disabled={!newSecondaryQuestion.newChoiceText.trim()}
                    >
                      <AddIcon />
                    </IconButton>
                  </Grid>
                </Grid>

                <Box sx={{ mt: 1 }}>
                  {newSecondaryQuestion.choices.map((choice) => (
                    <Box
                      key={choice.id}
                      sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
                    >
                      <TextField
                        fullWidth
                        value={choice.text}
                        onChange={(e) => {
                          const updatedChoices =
                            newSecondaryQuestion.choices.map((c) =>
                              c.id === choice.id
                                ? { ...c, text: e.target.value }
                                : c,
                            )
                          setNewSecondaryQuestion({
                            ...newSecondaryQuestion,
                            choices: updatedChoices,
                          })
                        }}
                        variant="outlined"
                        size="small"
                      />
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveSecondaryChoice(choice.id)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              </Grid>
            )}

            <Grid
              item
              xs={12}
              sx={{ display: 'flex', justifyContent: 'flex-end' }}
            >
              <Button
                onClick={() => setShowSecondaryForm(false)}
                color="secondary"
                text="Cancel"
                textColor="white"
                fullWidth={false}
                sx={{ mr: 1 }}
              />
              <Button
                onClick={handleAddSecondaryQuestion}
                color="primary"
                text="Add"
                textColor="white"
                fullWidth={false}
                disabled={!newSecondaryQuestion.question}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    )
  }

  const renderSecondaryQuestions = () => {
    return (
      <Grid item xs={12}>
        <Typography variant="subtitle1" sx={{ mt: 2 }}>
          Secondary Questions
        </Typography>
        <Box sx={{ mt: 1 }}>
          {renderSecondaryQuestionForm()}

          {childQuestions.map((childQuestion) => (
            <Card variant="outlined" sx={{ mb: 2 }} key={childQuestion.id}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={10}>
                    <Typography variant="subtitle2">
                      {childQuestion.question}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Type: {childQuestion.type} | Ask when:{' '}
                      {selectedType === 'CLOSED'
                        ? childQuestion.askSecondaryIf === 'yes'
                          ? 'Answered "Yes"'
                          : 'Answered "No"'
                        : `"${
                            choices.find((c) => c.id === childQuestion.askSecondaryIf)
                              ?.text
                          }" is selected`}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={2}
                    sx={{ display: 'flex', justifyContent: 'flex-end' }}
                  >
                    <IconButton
                      color="error"
                      onClick={() =>
                        handleRemoveSecondaryQuestion(childQuestion.id)
                      }
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                  <Grid item xs={12}>
                    {renderSecondaryQuestionEditor(childQuestion)}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Grid>
    )
  }

  return (
    <Grid
      container
      spacing={1}
      justifyContent="center"
      alignItems="center"
      sx={{
        padding: 2,
        borderRadius: 5,
      }}
    >
      <Grid item xs={12} xl={6}>
        <SelectInput
          name="siteId"
          control={control}
          options={sitesOptions}
          errors={errors}
          defaultValue={row?.site?.id}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <SelectInput
          name="target"
          control={control}
          options={[
            { value: '', label: 'Aucune cible' },
            { value: 'PATIENT', label: 'Patient' },
            { value: 'DOCTOR', label: 'Médecin' },
          ]}
          errors={errors}
          defaultValue={row?.target ?? ''}
        />
      </Grid>
      <Grid item xs={12}>
        <TextInput
          variant="outlined"
          name="question"
          errors={errors}
          control={control}
        />
      </Grid>
      <Grid item xs={12}>
        <SelectInput
          name="type"
          options={questionType}
          control={control}
          errors={errors}
        />
      </Grid>

      {isChoiceType && (
        <Grid item xs={12}>
          <Typography variant="subtitle1">Choices</Typography>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={10}>
              <TextField
                fullWidth
                value={newChoice}
                onChange={(e) => setNewChoice(e.target.value)}
                placeholder="Enter choice text"
                variant="outlined"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleAddChoice()
                  }
                }}
              />
            </Grid>
            <Grid item xs={2}>
              <IconButton
                color="primary"
                onClick={handleAddChoice}
                disabled={newChoice.trim() === ''}
              >
                <AddIcon />
              </IconButton>
            </Grid>
          </Grid>

          <Grid container spacing={1} sx={{ mt: 2 }}>
            {choices.map((choice) => {
              const dependentQuestions = getChildQuestionsForChoice(choice.id)
              const hasDependentQuestions = dependentQuestions.length > 0

              return (
                <Grid
                  item
                  xs={12}
                  key={choice.id}
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  <TextField
                    fullWidth
                    value={choice.text}
                    onChange={(e) => {
                      const updatedChoices = choices.map((c) =>
                        c.id === choice.id ? { ...c, text: e.target.value } : c,
                      )
                      setChoices(updatedChoices)
                    }}
                    variant="outlined"
                    size="small"
                  />
                  {hasDependentQuestions ? (
                    <PopConfirm
                      onConfirm={() => handleRemoveChoice(choice.id)}
                      title="Supprimer le choix et les questions dépendantes"
                      description={`Ce choix a ${dependentQuestions.length} question(s) secondaire(s) dépendante(s). Êtes-vous sûr de vouloir tout supprimer ?`}
                      okText="Supprimer tout"
                      cancelText="Annuler"
                    >
                      <IconButton color="error" size="small">
                        <DeleteIcon />
                      </IconButton>
                    </PopConfirm>
                  ) : (
                    <IconButton
                      color="error"
                      onClick={() => handleRemoveChoice(choice.id)}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Grid>
              )
            })}
          </Grid>
        </Grid>
      )}

      {canHaveSecondaryQuestions && renderSecondaryQuestions()}

      <Grid item xs={12}>
        <SpaceVertical size={5} />
        <Divider variant="middle" />
        <SpaceVertical size={5} />
      </Grid>

      {/* <Grid item xs={12}>
        <QuestionSelectionTable
          onChange={setSelectedExams}
          selectedRows={selectedExams}
        />
      </Grid> */}

      <Grid item xs={12}>
        <Grid container spacing={2} justifyContent="flex-end">
          <Grid item xs={2}>
            <Button
              onClick={handleCancelPress}
              color="secondary"
              text="Cancel"
              textColor="white"
              fullWidth
            />
          </Grid>
          <Grid item xs={2}>
            <Button
              onClick={handleSubmit(onSubmit)}
              color="primary"
              text="Save"
              textColor="white"
              fullWidth
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}
